import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';
import { css } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import AvatarItem from '@atlaskit/avatar/avatar-item';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import {
	AGENT_AVATAR_CLIP_PATH,
	AgentAvatar,
} from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import IconTile from '@atlaskit/icon/icon-tile';
import { token } from '@atlaskit/tokens';

import type { CharlieTableRow } from '@atlassian/charlie-table/use-charlie-table';

import { LoadableAfterPaint, LoadablePaint, type LoadableType } from '@confluence/loadable';

import { sortByState, type TableSorting } from '../AnalyticsTable/tableUtils.ts';
import { AnalyticsProfileIdentity } from '../AnalyticsProfileIdentity.tsx';

import { type AgentIconType } from './agentIconType.ts';
import type { ChatGptIconProps } from './ChatGptIcon.tsx';
import type { ClaudeIconProps } from './ClaudeIcon.tsx';
import type { CodexIconProps } from './CodexIcon.tsx';
import type { CursorIconProps } from './CursorIcon.tsx';
import type { DiaIconProps } from './DiaIcon.tsx';
import type { FigmaIconProps } from './FigmaIcon.tsx';
import type { GitHubCopilotIconProps } from './GitHubCopilotIcon.tsx';
import type { GrokIconProps } from './GrokIcon.tsx';
import type { HermesAgentIconProps } from './HermesAgentIcon.tsx';
import type { KiroIconProps } from './KiroIcon.tsx';
import type { OpenCodeIconProps } from './OpenCodeIcon.tsx';
import type { VisualStudioCodeIconProps } from './VisualStudioCodeIcon.tsx';
import { RovoIcon } from './RovoIcon.tsx';

/**
 * The Cursor icon inlines a large base64 raster, so it is code-split into its own
 * chunk (`loadable-CursorIcon`) and loaded after paint rather than statically
 * imported — it only renders for the rare `agentIcon === 'cursor'` case, so its
 * payload should not weigh down every chunk that includes this module.
 */
const CursorIcon: LoadableType<CursorIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CursorIcon" */ './CursorIcon.tsx')).CursorIcon,
});

/**
 * The Claude, ChatGPT and Figma brand icons only render for the rare
 * `agentIcon === 'claude'` / `'chatgpt'` / `'figma'` cases, so — like the Cursor icon —
 * they are code-split into their own chunks and loaded after paint rather than weighing
 * down every chunk that includes this module.
 */
const ClaudeIcon: LoadableType<ClaudeIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ClaudeIcon" */ './ClaudeIcon.tsx')).ClaudeIcon,
});

const ChatGptIcon: LoadableType<ChatGptIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ChatGptIcon" */ './ChatGptIcon.tsx')).ChatGptIcon,
});

const FigmaIcon: LoadableType<FigmaIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FigmaIcon" */ './FigmaIcon.tsx')).FigmaIcon,
});

/**
 * The Dia and Codex icons only render for the rare `agentIcon === 'dia'` / `'codex'`
 * cases, so — like the other brand icons — they are code-split into their own chunks and
 * loaded after paint.
 */
const DiaIcon: LoadableType<DiaIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-DiaIcon" */ './DiaIcon.tsx')).DiaIcon,
});

const CodexIcon: LoadableType<CodexIconProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CodexIcon" */ './CodexIcon.tsx')).CodexIcon,
});

const GrokIcon: LoadableType<GrokIconProps> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-GrokIcon" */ './GrokIcon.tsx')).GrokIcon,
});

const GitHubCopilotIcon: LoadableType<GitHubCopilotIconProps> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-GitHubCopilotIcon" */ './GitHubCopilotIcon.tsx'))
			.GitHubCopilotIcon,
});

const VisualStudioCodeIcon: LoadableType<VisualStudioCodeIconProps> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-VisualStudioCodeIcon" */ './VisualStudioCodeIcon.tsx'
			)
		).VisualStudioCodeIcon,
});

const OpenCodeIcon: LoadableType<OpenCodeIconProps> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-OpenCodeIcon" */ './OpenCodeIcon.tsx'))
			.OpenCodeIcon,
});

const HermesAgentIcon: LoadableType<HermesAgentIconProps> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-HermesAgentIcon" */ './HermesAgentIcon.tsx'))
			.HermesAgentIcon,
});

const KiroIcon: LoadableType<KiroIconProps> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-KiroIcon" */ './KiroIcon.tsx')).KiroIcon,
});

/**
 * Row-model mixin for tables that render AI-agent activity rows. Intersect it into a
 * table's own row data model. All fields are optional so existing (people) rows are
 * unaffected. Shared across the Page viewers table and the Space/Site users activity
 * tables.
 */
export type ExpandableAgentRowFields = {
	isAgent?: boolean; // render the hexagon AgentAvatar instead of a person avatar
	isChildRow?: boolean; // an indented person who used the agent
	agentId?: string; // stable id used for expand state + child lookup
	isExpandable?: boolean; // agent has child rows → show the expand chevron
	isExpanded?: boolean; // current expand state (injected by useExpandableAgentRows)
	agentSubtitle?: string; // e.g. "with Mathew Bertrand and 3 others"
	onToggleExpand?: () => void; // toggles expand state for this agentId
	agentIcon?: AgentIconType; // fixed fallback icon for unresolved agents
	agentAvatarUrl?: string; // resolved agent avatar image URL (from the agent account's `picture`)
};

export const agentActivityI18n: {
	expandAgentRow: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	collapseAgentRow: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	agentSubtitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	expandAgentRow: {
		id: 'confluence-analytics.agent-activity.expand-agent-row',
		defaultMessage: 'Show the people who used this agent',
		description:
			'Accessible label for the button that expands an AI agent row in an analytics table to reveal the people who used the agent.',
	},
	collapseAgentRow: {
		id: 'confluence-analytics.agent-activity.collapse-agent-row',
		defaultMessage: 'Hide the people who used this agent',
		description:
			'Accessible label for the button that collapses an expanded AI agent row in an analytics table.',
	},
	agentSubtitle: {
		id: 'confluence-analytics.agent-activity.agent-subtitle',
		defaultMessage: 'with {name}{count, plural, =0 {} one { and # other} other { and # others}}',
		description:
			'Subtitle shown under an AI agent row in an analytics table, listing the people who used the agent. {count} is the number of additional people beyond {name}: 0 renders just "with Mathew Bertrand", 1 renders "with Mathew Bertrand and 1 other", 2+ renders "with Mathew Bertrand and 3 others".',
	},
});

type AgentAvatarSize = 'small' | 'medium';
const AGENT_USER_DUAL_AVATAR_AGENT_SIZE: AgentAvatarSize = 'small';
const AGENT_USER_DUAL_AVATAR_USER_SIZE = 'xxsmall';
const unidentifiedAgentAvatarBackgroundStyles = css({
	// Scope Tile's default neutral background to the exact Figma value for this avatar only.
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-neutral': '#DDDEE1',
});

const DEFAULT_AVATAR_ITEM_COVER = { top: 0, left: 0, width: 32, height: 32 };

type AvatarItemCover = typeof DEFAULT_AVATAR_ITEM_COVER;

const dualAvatarCardTriggerStyles = css({
	position: 'absolute',
	display: 'block',
	pointerEvents: 'none',
	boxSizing: 'border-box',
	// Popper measures this Pressable, not the overlay. People-only rows wrap AvatarItem;
	// dual rows must make the same button fill that identity cell. The migrated trigger
	// uses `all: unset; display: inline` and testId `user-profile-card-trigger-wrapper`.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- override profile-card Pressable box
	'& [data-testid="user-profile-card-trigger-wrapper"], & [data-testid="profileCardTrigger"]': {
		display: 'block',
		width: '100%',
		height: '100%',
		pointerEvents: 'none',
	},
});

const useAvatarItemCover = (): {
	rootRef: React.RefObject<HTMLDivElement>;
	cover: AvatarItemCover;
} => {
	const rootRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
	const [cover, setCover] = useState<AvatarItemCover>(DEFAULT_AVATAR_ITEM_COVER);

	useLayoutEffect(() => {
		const root = rootRef.current;
		const identityCell = root?.parentElement;
		if (!root || !identityCell) {
			return;
		}

		const updateCover = () => {
			const identityRect = identityCell.getBoundingClientRect();
			const rootRect = root.getBoundingClientRect();
			if (identityRect.width === 0 && identityRect.height === 0) {
				return;
			}
			setCover({
				top: identityRect.top - rootRect.top,
				left: identityRect.left - rootRect.left,
				width: identityRect.width,
				height: identityRect.height,
			});
		};

		updateCover();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(updateCover);
		observer.observe(identityCell);
		observer.observe(root);
		return () => observer.disconnect();
	}, [rootRef]);

	return { rootRef, cover };
};

const styles = cssMap({
	toggleSmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		boxSizing: 'border-box',
		overflow: 'hidden',
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	toggleMedium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		boxSizing: 'border-box',
		overflow: 'hidden',
		marginInlineEnd: token('space.025'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	childIndent: {
		paddingInlineStart: token('space.500'),
	},
	iconWrapperSmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
	},
	iconWrapperMedium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
	},
	iconWrapperSmallNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
	},
	iconWrapperMediumNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
	},
	unidentifiedAgentAvatarSmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		marginInlineStart: token('space.025'),
		marginInlineEnd: token('space.025'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarMedium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarSmallNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	unidentifiedAgentAvatarMediumNoSpacing: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
	brandAgentTile: {
		// Gray hexagon tile behind a brand mark (Figma/Dia/Codex), matching the gray
		// unidentified-agent avatar so brand icons read as the same hexagon shape.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
		backgroundColor: token('color.background.neutral'),
	},
	avatarOffsetInlineStart: {
		transform: 'translateX(2px)',
	},
	dualAvatar: {
		position: 'relative',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
	},
	dualAvatarOffsetInlineStart: {
		position: 'relative',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		marginInlineEnd: token('space.025'),
		transform: 'translateX(2px)',
	},
	dualAvatarAgent: {
		position: 'absolute',
		top: token('space.0'),
		insetInlineStart: token('space.0'),
		width: '20px',
		height: '20px',
		transform: 'scale(0.8333333333333334)',
		transformOrigin: 'top left',
	},
	dualAvatarUser: {
		position: 'absolute',
		top: token('space.150'),
		insetInlineEnd: token('space.025'),
		marginBlockStart: token('space.025'),
		borderRadius: token('radius.full'),
		backgroundColor: token('elevation.surface'),
	},
	dualAvatarTriggerFill: {
		position: 'relative',
		width: '100%',
		height: '100%',
		display: 'block',
		pointerEvents: 'none',
	},
	dualAvatarVisualStack: {
		position: 'absolute',
		width: '32px',
		height: '32px',
		pointerEvents: 'none',
	},
	dualAvatarTriggerTarget: {
		pointerEvents: 'auto',
	},
});

const AgentAvatarContainer = ({
	avatarSize,
	hasInlineSpacing,
	children,
}: {
	avatarSize: AgentAvatarSize;
	hasInlineSpacing: boolean;
	children: React.ReactNode;
}): React.JSX.Element => {
	if (avatarSize === 'medium' && hasInlineSpacing) {
		return <Box xcss={styles.iconWrapperMedium}>{children}</Box>;
	}

	if (avatarSize === 'medium') {
		return <Box xcss={styles.iconWrapperMediumNoSpacing}>{children}</Box>;
	}

	if (hasInlineSpacing) {
		return <Box xcss={styles.iconWrapperSmall}>{children}</Box>;
	}

	return <Box xcss={styles.iconWrapperSmallNoSpacing}>{children}</Box>;
};

const UnidentifiedAgentAvatar = ({
	avatarSize,
	hasInlineSpacing,
}: {
	avatarSize: AgentAvatarSize;
	hasInlineSpacing: boolean;
}): React.JSX.Element => {
	const icon = (
		<span
			data-testid="unidentified-agent-avatar-background"
			css={unidentifiedAgentAvatarBackgroundStyles}
		>
			<IconTile
				icon={AiAgentIcon}
				label=""
				appearance="gray"
				size={avatarSize === 'medium' ? 'medium' : 'small'}
			/>
		</span>
	);

	if (avatarSize === 'medium' && hasInlineSpacing) {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarMedium}>
				{icon}
			</Box>
		);
	}

	if (avatarSize === 'medium') {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarMediumNoSpacing}>
				{icon}
			</Box>
		);
	}

	if (hasInlineSpacing) {
		return (
			<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarSmall}>
				{icon}
			</Box>
		);
	}

	return (
		<Box testId="unidentified-agent-avatar" xcss={styles.unidentifiedAgentAvatarSmallNoSpacing}>
			{icon}
		</Box>
	);
};

/**
 * Renders an agent row's leading icon: the fixed AiAgent/Rovo icon for agents that
 * couldn't be resolved to an account, otherwise the deterministic hexagon AgentAvatar
 * (or, when the resolved agent account has a `picture`, that avatar image inside the
 * hexagon).
 */
export const AgentRowAvatar = ({
	name,
	agentIcon,
	avatarUrl,
	avatarSize = 'small',
	isOffsetInlineStart = false,
	hasInlineSpacing = true,
}: {
	name?: string;
	agentIcon?: AgentIconType;
	avatarUrl?: string;
	avatarSize?: AgentAvatarSize;
	isOffsetInlineStart?: boolean;
	hasInlineSpacing?: boolean;
}): React.JSX.Element => {
	const avatar = (() => {
		if (agentIcon === 'ai-agent') {
			return (
				<UnidentifiedAgentAvatar avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing} />
			);
		}
		if (agentIcon === 'rovo') {
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					<RovoIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</AgentAvatarContainer>
			);
		}
		if (agentIcon === 'cursor') {
			return (
				<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
					<CursorIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</AgentAvatarContainer>
			);
		}
		if (agentIcon === 'claude') {
			return (
				<Box xcss={avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall}>
					<ClaudeIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'chatgpt') {
			return (
				<Box xcss={avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall}>
					<ChatGptIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'figma') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<FigmaIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'dia') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<DiaIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'codex') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<CodexIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'grok') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<GrokIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'github-copilot') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<GitHubCopilotIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'vscode') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<VisualStudioCodeIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'open-code') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<OpenCodeIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'hermes') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<HermesAgentIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		if (agentIcon === 'kiro') {
			return (
				<Box
					xcss={cx(
						avatarSize === 'medium' ? styles.iconWrapperMedium : styles.iconWrapperSmall,
						styles.brandAgentTile,
					)}
				>
					<KiroIcon label="" size={avatarSize === 'medium' ? 32 : 24} />
				</Box>
			);
		}
		return (
			<AgentAvatarContainer avatarSize={avatarSize} hasInlineSpacing={hasInlineSpacing}>
				<AgentAvatar size={avatarSize} name={name} label="" imageUrl={avatarUrl} />
			</AgentAvatarContainer>
		);
	})();

	return isOffsetInlineStart ? (
		<Box testId="agent-avatar-offset" xcss={styles.avatarOffsetInlineStart}>
			{avatar}
		</Box>
	) : (
		avatar
	);
};

/** Agent avatar anchored top-left, overlapped by the related person's avatar bottom-right. */
export const AgentUserDualAvatar = ({
	agentName,
	agentIcon,
	agentAvatarUrl,
	agentUserId,
	userName,
	userAvatarUrl,
	relatedUserId,
	isOffsetInlineStart = false,
}: {
	agentName?: string;
	agentIcon?: AgentIconType;
	agentAvatarUrl?: string;
	agentUserId?: string;
	userName?: string;
	userAvatarUrl?: string;
	relatedUserId?: string;
	isOffsetInlineStart?: boolean;
}): React.JSX.Element => {
	const agentAvatar = (
		<AgentRowAvatar
			name={agentName}
			agentIcon={agentIcon}
			avatarUrl={agentAvatarUrl}
			avatarSize={AGENT_USER_DUAL_AVATAR_AGENT_SIZE}
			hasInlineSpacing={false}
		/>
	);
	const userAvatar = (
		<Avatar
			src={userAvatarUrl}
			name={userName}
			size={AGENT_USER_DUAL_AVATAR_USER_SIZE}
			borderColor="transparent"
			label=""
		/>
	);

	return (
		<DualAvatarWithIdentityCards
			agentName={agentName}
			agentUserId={agentUserId}
			userName={userName}
			relatedUserId={relatedUserId}
			isOffsetInlineStart={isOffsetInlineStart}
			agentAvatar={agentAvatar}
			userAvatar={userAvatar}
		/>
	);
};

const DualAvatarWithIdentityCards = ({
	agentName,
	agentUserId,
	userName,
	relatedUserId,
	isOffsetInlineStart,
	agentAvatar,
	userAvatar,
}: {
	agentName?: string;
	agentUserId?: string;
	userName?: string;
	relatedUserId?: string;
	isOffsetInlineStart: boolean;
	agentAvatar: React.ReactNode;
	userAvatar: React.ReactNode;
}): React.JSX.Element => {
	const { rootRef, cover } = useAvatarItemCover();
	const coverStyle: React.CSSProperties = {
		top: cover.top,
		left: cover.left,
		width: cover.width,
		height: cover.height,
	};
	const visualStackStyle: React.CSSProperties = {
		top: -cover.top,
		left: -cover.left,
	};

	return (
		<Box
			ref={rootRef}
			testId="agent-user-dual-avatar"
			xcss={isOffsetInlineStart ? styles.dualAvatarOffsetInlineStart : styles.dualAvatar}
			{...{ 'data-analytics-card-anchor': 'avatar-item' }}
		>
			<div
				css={dualAvatarCardTriggerStyles}
				data-testid="agent-user-dual-avatar-agent-anchor"
				data-analytics-card-anchor="avatar-item"
				style={coverStyle}
			>
				<AnalyticsProfileIdentity userId={agentUserId} fullName={agentName} agent={{}}>
					<Box
						testId="agent-user-dual-avatar-agent-trigger"
						xcss={styles.dualAvatarTriggerFill}
						{...{
							'data-analytics-card-fill': 'avatar-item',
							'data-analytics-card-pointer': 'none',
						}}
					>
						<Box xcss={styles.dualAvatarVisualStack} style={visualStackStyle}>
							<Box
								testId="agent-user-dual-avatar-agent-target"
								xcss={cx(styles.dualAvatarAgent, styles.dualAvatarTriggerTarget)}
								{...{ 'data-analytics-card-pointer': 'auto' }}
							>
								{agentAvatar}
							</Box>
						</Box>
					</Box>
				</AnalyticsProfileIdentity>
			</div>
			<div
				css={dualAvatarCardTriggerStyles}
				data-testid="agent-user-dual-avatar-user-anchor"
				data-analytics-card-anchor="avatar-item"
				style={coverStyle}
			>
				<AnalyticsProfileIdentity userId={relatedUserId} fullName={userName}>
					<Box
						testId="agent-user-dual-avatar-user-trigger"
						xcss={styles.dualAvatarTriggerFill}
						{...{
							'data-analytics-card-fill': 'avatar-item',
							'data-analytics-card-pointer': 'none',
						}}
					>
						<Box xcss={styles.dualAvatarVisualStack} style={visualStackStyle}>
							<Box
								testId="agent-user-dual-avatar-user-target"
								xcss={cx(styles.dualAvatarUser, styles.dualAvatarTriggerTarget)}
								{...{ 'data-analytics-card-pointer': 'auto' }}
							>
								{userAvatar}
							</Box>
						</Box>
					</Box>
				</AnalyticsProfileIdentity>
			</div>
		</Box>
	);
};

/**
 * The agent's hexagon avatar doubles as the expand affordance: on hover or keyboard
 * focus it swaps to a chevron, and clicking it toggles the nested child rows. Keeping
 * the toggle on the avatar itself (rather than a separate inline control) matches the
 * design.
 */
export const AgentAvatarExpandToggle = ({
	name,
	isExpanded,
	onToggle,
	expandLabel,
	collapseLabel,
	agentIcon,
	avatarUrl,
	avatarSize = 'small',
	isOffsetInlineStart = false,
}: {
	name?: string;
	isExpanded: boolean;
	onToggle: () => void;
	expandLabel: string;
	collapseLabel: string;
	agentIcon?: AgentIconType;
	avatarUrl?: string;
	avatarSize?: AgentAvatarSize;
	isOffsetInlineStart?: boolean;
}): React.JSX.Element => {
	const [isActive, setIsActive] = useState(false);
	const showChevron = isActive || isExpanded;

	return (
		<Pressable
			xcss={avatarSize === 'medium' ? styles.toggleMedium : styles.toggleSmall}
			aria-label={isExpanded ? collapseLabel : expandLabel}
			aria-expanded={isExpanded}
			onClick={(event) => {
				event.stopPropagation();
				onToggle();
			}}
			onMouseEnter={() => setIsActive(true)}
			onMouseLeave={() => setIsActive(false)}
			onFocus={() => setIsActive(true)}
			onBlur={() => setIsActive(false)}
		>
			{showChevron ? (
				isExpanded ? (
					<ChevronDownIcon label="" />
				) : (
					<ChevronRightIcon label="" />
				)
			) : (
				<AgentRowAvatar
					name={name}
					agentIcon={agentIcon}
					avatarUrl={avatarUrl}
					avatarSize={avatarSize}
					isOffsetInlineStart={isOffsetInlineStart}
				/>
			)}
		</Pressable>
	);
};

/**
 * Renders the name-column cell for an AI-agent row: the hexagon AgentAvatar (which
 * doubles as an expand toggle when the agent has child rows) plus an optional subtitle.
 * The consuming table supplies `name`/`subtitle` from its own row model.
 */
export const AgentActivityNameCell = ({
	name,
	subtitle,
	isExpandable,
	isExpanded,
	onToggleExpand,
	agentIcon,
	avatarUrl,
	avatarSize = 'small',
	isOffsetInlineStart = false,
}: {
	name?: string;
	subtitle?: string;
	isExpandable?: boolean;
	isExpanded?: boolean;
	onToggleExpand?: () => void;
	agentIcon?: AgentIconType;
	avatarUrl?: string;
	avatarSize?: AgentAvatarSize;
	isOffsetInlineStart?: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const avatar = isExpandable ? (
		<AgentAvatarExpandToggle
			name={name}
			isExpanded={Boolean(isExpanded)}
			onToggle={onToggleExpand!}
			expandLabel={formatMessage(agentActivityI18n.expandAgentRow)}
			collapseLabel={formatMessage(agentActivityI18n.collapseAgentRow)}
			agentIcon={agentIcon}
			avatarUrl={avatarUrl}
			avatarSize={avatarSize}
			isOffsetInlineStart={isOffsetInlineStart}
		/>
	) : (
		<AgentRowAvatar
			name={name}
			agentIcon={agentIcon}
			avatarUrl={avatarUrl}
			avatarSize={avatarSize}
			isOffsetInlineStart={isOffsetInlineStart}
		/>
	);

	return <AvatarItem avatar={avatar} primaryText={name} secondaryText={subtitle} />;
};

/** Wraps an agent's nested child (person) cell with a left indent. */
export const AgentChildRowIndent = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => <Box xcss={styles.childIndent}>{children}</Box>;

/**
 * Owns expand/collapse state and flattens parent rows into the array handed to the
 * table: each expanded agent's children are spliced directly beneath it, so sorting
 * and pagination (done by the caller) only ever act on the parent rows.
 *
 * When `sorting` is provided, an expanded agent's child (related-user) rows are sorted
 * by the same column/direction the table is sorted by, so the nested rows stay
 * consistent with the parent ordering.
 */
export const useExpandableAgentRows = <TableDataModel extends ExpandableAgentRowFields>(
	parentRows: CharlieTableRow<TableDataModel>[],
	childrenByAgentId: Record<string, CharlieTableRow<TableDataModel>[]>,
	sorting?: TableSorting<TableDataModel>,
): CharlieTableRow<TableDataModel>[] => {
	const [expandedAgentIds, setExpandedAgentIds] = useState<Set<string>>(() => new Set());

	const toggleAgentExpansion = useCallback((agentId: string) => {
		setExpandedAgentIds((previous) => {
			const next = new Set(previous);
			if (next.has(agentId)) {
				next.delete(agentId);
			} else {
				next.add(agentId);
			}
			return next;
		});
	}, []);

	return useMemo(() => {
		const rows: CharlieTableRow<TableDataModel>[] = [];

		parentRows.forEach((row) => {
			const { isAgent, isExpandable, agentId } = row.data;
			if (isAgent && isExpandable && agentId) {
				const isExpanded = expandedAgentIds.has(agentId);
				rows.push({
					...row,
					data: {
						...row.data,
						isExpanded,
						onToggleExpand: () => toggleAgentExpansion(agentId),
					},
				});
				if (isExpanded) {
					const children = childrenByAgentId[agentId] ?? [];
					rows.push(...(sorting ? [...children].sort(sortByState(sorting)) : children));
				}
			} else {
				rows.push(row);
			}
		});

		return rows;
	}, [parentRows, childrenByAgentId, expandedAgentIds, toggleAgentExpansion, sorting]);
};

export const getAgentSubtitle = (
	formatMessage: IntlShape['formatMessage'],
	name: string,
	count: number,
): string =>
	formatMessage(agentActivityI18n.agentSubtitle, {
		name,
		count,
	});
